import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { getCountryName, getCountryOptions, type CountryOption } from '../lib/country-options.ts';

import { IconCheck, IconSearch } from './icons.tsx';
import { BottomSheet } from './sheet.tsx';

function foldForMatch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/** Matches the name (accent-insensitive) or the raw code — a returning user
 * who still remembers the old 2-letter field can type that too. */
export function filterCountryOptions(
  options: readonly CountryOption[],
  query: string,
): CountryOption[] {
  const needle = foldForMatch(query.trim());
  if (needle === '') return [...options];
  return options.filter(
    (option) =>
      foldForMatch(option.label).includes(needle) || option.value.toLowerCase().includes(needle),
  );
}

function byLabel(a: CountryOption, b: CountryOption): number {
  return a.label.localeCompare(b.label);
}

interface CountryPickerProps {
  readonly id: string;
  /** Alpha-2 code, or '' when nothing is chosen. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Fired once the picker closes, whichever way — mirrors the other
   * fields' onBlur-marks-touched wiring without firing mid-choice. */
  readonly onBlur?: () => void;
  readonly 'aria-invalid'?: boolean | undefined;
  readonly 'aria-describedby'?: string | undefined;
}

export const CountryPicker = ({
  id,
  value,
  onChange,
  onBlur,
  ...aria
}: CountryPickerProps): ReactNode => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const reactId = useId();
  const listboxId = `${id || reactId}-listbox`;

  const options = useMemo(() => getCountryOptions().sort(byLabel), []);
  const rows = useMemo(() => filterCountryOptions(options, term), [options, term]);
  const displayValue = value === '' ? '' : (getCountryName(value) ?? value);

  useEffect(() => {
    if (!open) return undefined;
    // Longer than BottomSheet's own 50ms close-button focus (sheet.tsx) so
    // this always lands last: the sheet's own contract still holds (close
    // button briefly focused first), the search box just ends up focused.
    const t = window.setTimeout(() => searchRef.current?.focus(), 60);
    return (): void => {
      window.clearTimeout(t);
    };
  }, [open]);

  function openSheet(): void {
    setTerm('');
    setHighlighted(null);
    setOpen(true);
  }

  function closeSheet(): void {
    setOpen(false);
    onBlur?.();
  }

  function select(option: CountryOption): void {
    onChange(option.value);
    closeSheet();
  }

  function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (rows.length === 0) return;
      const idx = highlighted === null ? -1 : rows.findIndex((r) => r.value === highlighted);
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const next = idx < 0 ? (step === 1 ? 0 : rows.length - 1) : (idx + step + rows.length) % rows.length;
      setHighlighted(rows[next]?.value ?? null);
      return;
    }
    if (e.key === 'Enter') {
      const option = rows.find((r) => r.value === highlighted);
      if (option === undefined) return;
      e.preventDefault();
      select(option);
    }
  }

  return (
    <>
      <input
        id={id}
        type="text"
        // Read-only, not disabled: it stays in the tab order and keeps
        // showing/announcing the current value, it just never takes typed
        // input directly — tapping/opening always goes through the sheet.
        readOnly
        value={displayValue}
        placeholder="Select a country"
        onClick={openSheet}
        // Not onFocus: BottomSheet restores focus to this trigger on close,
        // so an onFocus-triggered open would immediately reopen the sheet
        // on every close, making it impossible to dismiss.
        onKeyDown={(e): void => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openSheet();
          }
        }}
        // Only when still closed: closeSheet() already fires onBlur once,
        // and opening moves focus into the sheet, which would blur this
        // trigger too — this covers tabbing past it without ever opening.
        onBlur={(): void => {
          if (!open) onBlur?.();
        }}
        aria-haspopup="dialog"
        aria-required
        {...aria}
      />
      {/* Portalled out of the trigger's <label>: an un-portalled sheet
          nested inside that label would fold its own "Country" title text
          into the trigger's accessible name (read as "Country Country"). */}
      {createPortal(
        <BottomSheet open={open} title="Country" onClose={closeSheet}>
          <div className="country-picker-search">
            <IconSearch className="country-picker-search-icon" />
            <input
              ref={searchRef}
              type="text"
              className="country-picker-search-input"
              placeholder="Search countries…"
              value={term}
              onChange={(e): void => {
                const next = e.target.value;
                setTerm(next);
                const filtered = filterCountryOptions(options, next);
                setHighlighted(next.trim() === '' ? null : (filtered[0]?.value ?? null));
              }}
              onKeyDown={onSearchKeyDown}
              role="combobox"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={
                highlighted !== null ? `${listboxId}-option-${highlighted}` : undefined
              }
            />
          </div>
          <ul id={listboxId} role="listbox" aria-label="Countries" className="country-picker-list">
            {rows.map((option) => (
              <li
                key={option.value}
                id={`${listboxId}-option-${option.value}`}
                role="option"
                aria-selected={option.value === value}
                data-highlighted={option.value === highlighted || undefined}
                className="country-picker-row"
                onMouseDown={(e): void => {
                  e.preventDefault();
                  select(option);
                }}
                onMouseEnter={(): void => {
                  setHighlighted(option.value);
                }}
              >
                <span>{option.label}</span>
                {option.value === value ? <IconCheck className="country-picker-check" /> : null}
              </li>
            ))}
          </ul>
          {rows.length === 0 ? (
            <div className="country-picker-empty" role="status">
              No countries match
            </div>
          ) : null}
        </BottomSheet>,
        document.body,
      )}
    </>
  );
};
